// The registry (instruction/1, §7 and §20): every built-in block and inline kind, its body, forms
// and typed attributes, the rule keywords and the grammar's patterns. The data is the
// specification's registry.json, vendored unchanged (specification/registry.json); this module only
// types it and answers the questions the reader, the serializer and the renderer ask of it.
import data from "../specification/registry.json";

export type Form = "container" | "leaf" | "keyword" | "alert";
export type Family = "guidance" | "advisory" | "contract" | "connected" | "resource" | "technical" | "layout" | "presentation" | "composition";
export type BodyFormat = "markdown" | "yaml" | "json" | "csv" | "text" | "html" | "code";

/** The JSON-Schema subset the registry uses for an attribute. `{}` accepts any value. */
export interface AttrSpec { type?: "string" | "boolean" | "integer" | "number" | "array"; enum?: string[]; pattern?: string; minimum?: number; maximum?: number; items?: { type: string } }

export interface KindSpec {
  body: "markdown" | "yaml" | "none";
  forms: Form[];
  attributes: Record<string, AttrSpec>;
  required: string[];
  family: Family;
  /** `always` is `must`, `avoid` is `should` with `not`, `info` is `note` (§6.1). */
  alias?: string;
  flags?: Record<string, unknown>;
  /** A `column` lives in `columns`, a `tab` in `tabs`, a `step` in `steps` (§8). */
  parent?: string;
  /** `example`: its body is quoted Markdown, never scanned for Instruction syntax (§6.2). */
  opaque?: boolean;
  /** The body formats a `format` attribute may choose; absent = the default body only. */
  formats?: BodyFormat[];
}

const registry = data as unknown as {
  format: string; spec: string;
  common_attributes: Record<string, AttrSpec>;
  kinds: Record<string, KindSpec>;
  inline: Record<string, Record<string, AttrSpec>>;
  keywords: Record<string, string>;
  keyword_flags: Record<string, Record<string, unknown>>;
  grammar: { kind: string; id: string; parameter: string; digest: string };
};

/** A table looked up by what a document writes — a kind, a key — with no prototype: `constructor` or `toString` is a
 *  name it doesn't have, never one of Object's. */
export function table<T>(o: Record<string, T>, each: (v: T) => T = (v) => v): Record<string, T> {
  const out = Object.create(null) as Record<string, T>;
  for (const [k, v] of Object.entries(o)) out[k] = each(v);
  return out;
}

export const KINDS: Readonly<Record<string, KindSpec>> = table(registry.kinds, (k) => ({ ...k, attributes: table(k.attributes) }));
export const COMMON: Readonly<Record<string, AttrSpec>> = table(registry.common_attributes);
export const INLINE: Readonly<Record<string, Record<string, AttrSpec>>> = table(registry.inline, (a) => table(a));
/** Authored keyword → normalized kind: `MUST NOT` → `never`, `ALWAYS` → `must`, `INFO` → `note`. */
export const KEYWORDS: Readonly<Record<string, string>> = table(registry.keywords);
/** `SHOULD NOT` and `AVOID` keep their negation as `not: true`. */
export const KEYWORD_FLAGS: Readonly<Record<string, Record<string, unknown>>> = table(registry.keyword_flags);

export const PATTERNS = {
  kind: new RegExp(registry.grammar.kind),
  id: new RegExp(registry.grammar.id),
  parameter: new RegExp(registry.grammar.parameter),
  digest: new RegExp(registry.grammar.digest),
};

/** The operative rules (§6.1), aliases included. */
export const RULES: ReadonlySet<string> = new Set(["must", "should", "may", "never", "guardrail", "always", "avoid"]);
/** The inline kinds that reference an object and must match the target's kind (§11.1); `ref` is generic. */
export const TYPED_REFERENCES: ReadonlySet<string> = new Set(["person", "role", "skill", "mcp", "tool", "agent", "service", "source", "resource"]);
/** Inline kinds whose `ref`, `href` or `title` is required (reference/markdown.md, inline annotations). */
export const INLINE_REQUIRED: Readonly<Record<string, string>> = table({ ref: "ref", person: "ref", role: "ref", skill: "ref", mcp: "ref", tool: "ref", agent: "ref", service: "ref", source: "ref", resource: "ref", cite: "ref", button: "href", abbr: "title" });
/** The front matter's keys this edition defines (§3.1); any other is kept and means nothing to the core. */
export const FRONT_MATTER_KEYS: readonly string[] = [
  "spec", "id", "version", "title", "description", "language", "tags", "license", "parameters", "extends", "extensions", "references", "resolvers",
  "exports", "presentation", "requires",
];
/** The backmatter's known first keys (§3.2 rule 2). */
export const BACKMATTER_KEYS: readonly string[] = ["authors", "owners", "review", "history", "sources", "approvals", "notes", "test_results"];
/** Attributes a generated text unit or a layout leaves out: they are presentation (§14.2). */
export const PRESENTATION_ATTRS: ReadonlySet<string> = new Set(["class", "style", "count", "columns", "span", "open"]);

/** A kind's normalized meaning: `always` reads as `must`, `info` as `note`. */
export function canonicalKind(kind: string): string {
  return KINDS[kind]?.alias ?? kind;
}

/** The attribute spec a key has on a kind: the kind's own, then the common attributes. */
export function attrSpec(kind: string, key: string): AttrSpec | undefined {
  return KINDS[kind]?.attributes[key] ?? COMMON[key];
}

/** The attribute spec a key has on an inline kind: its own, plus `class`, `style` and `id`. */
export function inlineAttrSpec(kind: string, key: string): AttrSpec | undefined {
  const own = INLINE[kind]?.[key];
  if (own) return own;
  if (key === "class" || key === "style" || key === "id") return COMMON[key];
  return undefined;
}

export const registryVersion = { format: registry.format, spec: registry.spec };
